import { useCallback } from 'react';
import { useApolloClient } from '@apollo/react-hooks';
import { ApolloError } from 'apollo-client';
import { REMOTE_ISSUE_LINKS_TYPE } from '@atlassian/jira-business-constants';
import { shouldHandleTheError } from '@atlassian/jira-business-error-handling';
import { expValEquals } from '@atlassian/jira-feature-experiments';
import { useCloudId } from '@atlassian/jira-tenant-context-controller';
import type {
	/* eslint-disable-next-line camelcase */
	JIS2JWMBoardItems_jira_jwmViewItems_JiraWorkManagementViewItemConnection_edges_node_fieldsByIdOrAlias,
	JIS2JWMBoardItems,
	/* eslint-disable-next-line camelcase */
	JIS2JWMBoardItems_jira_jwmViewItems_JiraWorkManagementViewItemConnection_edges_node,
	JIS2JWMBoardItemsVariables,
} from '../board-issues/__generated_apollo__/JIS2JWMBoardItems';
import { JIS2_JWMBoardItemsQuery } from '../board-issues/gql';

/** @deprecated Remove when jwm.board-awesome-enhancements is cleaned up */
// eslint-disable-next-line jira/ff/inline-usage
export const isWithIssueLinksEnabled = () =>
	expValEquals('jwm_linked_issues_on_the_board_milestone_1', 'cohort', 'variation');

const ITEMS_PER_PAGE = 100;

/** @deprecated Remove when jwm.board-awesome-enhancements is cleaned up */
export type IssueDetailsJIS2 =
	/* eslint-disable-next-line camelcase */
	JIS2JWMBoardItems_jira_jwmViewItems_JiraWorkManagementViewItemConnection_edges_node;

/** @deprecated Remove when jwm.board-awesome-enhancements is cleaned up */
export type IssueDetailFieldJIS2 =
	/* eslint-disable-next-line camelcase */
	JIS2JWMBoardItems_jira_jwmViewItems_JiraWorkManagementViewItemConnection_edges_node_fieldsByIdOrAlias;

// delete buildVariablesArray when cleaning up jwm.board-awesome-enhancements
// Build variables for queries in a way that responses won't exceed max item count.
// Exm: Given 350 issues, Query 1 (Issues 1-100), Query 2 (Issues 101-200), Query 3 (Issues 201-300), Query 4 (Issues 301-350)
const buildVariablesArray = ({
	fieldIds,
	issueIds,
	withConfluenceLinks,
	withCoverMedia,
	cloudId,
	withIssueLinks,
}: {
	fieldIds: Array<string>;
	issueIds: Array<number>;
	withConfluenceLinks: boolean;
	withCoverMedia: boolean;
	cloudId: string;
	withIssueLinks: boolean;
}) => {
	// eslint-disable-next-line @typescript-eslint/no-explicit-any
	let queryIssueIds: Array<any> = [];
	const variablesArray: Array<JIS2JWMBoardItemsVariables> = [];
	const pushIssueIdsToVariables = () => {
		variablesArray.push({
			fieldIds,
			first: ITEMS_PER_PAGE,
			jql: `id in (${queryIssueIds.join(',')})`,
			withConfluenceLinks,
			withCoverMedia,
			cloudId,
			withIssueLinks,
			withChildrenCount: false,
			withExtendedIssue: false,
		});
		queryIssueIds = [];
	};
	issueIds.forEach((issueId) => {
		queryIssueIds.push(issueId);
		if (queryIssueIds.length === ITEMS_PER_PAGE) {
			pushIssueIdsToVariables();
		}
	});
	if (queryIssueIds.length !== 0) {
		pushIssueIdsToVariables();
	}
	return variablesArray;
};

/** @deprecated Remove when jwm.board-awesome-enhancements is cleaned up */
export const useBoardIssuesOld = ({
	withConfluenceLinks,
	withIssueLinks,
	withCoverMedia,
}: {
	withConfluenceLinks: boolean;
	withIssueLinks: boolean;
	withCoverMedia: boolean;
}) => {
	const client = useApolloClient();
	const cloudId = useCloudId();

	const loadIssuesWithFields = useCallback(
		async ({
			issueIds,
			fieldIds,
			onLoad,
		}: {
			issueIds: number[];
			fieldIds: string[];
			onLoad: (arg1: IssueDetailsJIS2[]) => void;
		}): Promise<void> => {
			const variablesArray = buildVariablesArray({
				fieldIds: [
					...fieldIds
						.filter((fieldId) => fieldId !== REMOTE_ISSUE_LINKS_TYPE)
						// TODO After JIS2 migration completion, remove this mapping JET-4810
						.map((fieldId) => (fieldId === 'timeoriginalestimate' ? 'timetracking' : fieldId)),
				],
				issueIds,
				withConfluenceLinks,
				withCoverMedia,
				cloudId,
				withIssueLinks,
			});

			await Promise.all(
				variablesArray.map(async (variables) => {
					const response = await client.query<JIS2JWMBoardItems, JIS2JWMBoardItemsVariables>({
						query: JIS2_JWMBoardItemsQuery,
						fetchPolicy: 'no-cache',
						errorPolicy: 'all',
						variables,
					});

					if (!response) {
						return;
					}

					if (response.errors) {
						const apolloError = new ApolloError({
							graphQLErrors: response.errors,
						});

						if (shouldHandleTheError(apolloError)) {
							throw apolloError;
						}
					}

					const responseData = response.data.jira?.jwmViewItems;

					if (
						responseData?.__typename === 'JiraWorkManagementViewItemConnection' &&
						responseData.edges != null
					) {
						onLoad(
							responseData.edges
								.map((edge) => edge?.node)
								.filter((node): node is IssueDetailsJIS2 => node != null),
						);
					}
				}),
			);
		},
		[client, cloudId, withConfluenceLinks, withCoverMedia, withIssueLinks],
	);

	return {
		loadIssuesWithFields,
	};
};
